<template>
  <div id="main">
    <div id="manu">
      <navigation />
    </div>
    <div id="contentBox">
      <div class="registervideoContainer"></div>
      <div class="w">
        <div class="edit-container">
          <div class="header">
            <button @click="navigateBack"> {{ $t('back') }} </button>
            <h1>{{ isEditMode ? $t('edit') : $t('AddNewRecord') }}</h1>
            <button @click="clearInputs" :disabled="isSaving">{{ $t('clear') }}</button>
          </div>
          
          <form @submit.prevent="saveNotepad">
            <div class="content">
              <input
                v-model="formData.title" 
                :placeholder="$t('Pleaseenteratitle')"
                maxlength="150"
              >
              <!-- Quill 编辑器 -->
              <div id="editor" class="quill-editor"></div>
              <div class="image-upload">
                <label>{{ $t('Uploadimage') }}</label>
                <div class="upload-controls">
                  <input
                    type="file" 
                    accept="image/png,image/jpeg" 
                    multiple 
                    @change="handleImageUpload" 
                    class="file-input"
                  >
                  <div 
                    class="paste-area" 
                    @paste="handleImagePaste" 
                    contenteditable="true"
                    @keydown="preventInput"
                    :placeholder="$t('PasteImage')"
                  >{{ $t('PasteImage') }}</div>
                </div>
                
                <div v-if="imagePreviews.length" class="image-preview">
                  <div v-for="(preview, previewIndex) in imagePreviews" :key="previewIndex" class="preview-item">
                    <img 
                      crossorigin="anonymous" 
                      :src="preview" 
                      :alt="$t('ImagePreview')" 
                      @click="showEnlargedImage(preview)"
                      class="preview-img"
                    />
                    <button type="button" @click="removeImage(previewIndex)">{{ $t('delete') }}</button>
                    <div v-if="uploadProgress.images[previewIndex]" class="progress-bar">
                      <progress :value="uploadProgress.images[previewIndex]" max="100"></progress>
                      <span>{{ uploadProgress.images[previewIndex] }}% ({{ formatFileSize(formData.images[previewIndex]?.size || 0) }})</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="formData.image_urls.length" class="image-error">
                  <p v-for="(url, urlIndex) in formData.image_urls" :key="urlIndex">
                    {{ $t('Theimage') }}: {{ url }}
                  </p>
                </div>
              </div>
              <div class="file-upload">
                <label>{{ $t('Uploadfiles') }}</label>
                <div class="upload-controls">
                  <input 
                    type="file"
                    accept=".doc,.docx,.exe,.apk,.zip,.rar,.7z,.txt,.msi,.mp4" 
                    multiple 
                    @change="handleFileUpload" 
                    class="file-input"
                  >
                </div>
                <div v-if="filePreviews.length" class="file-preview">
                  <div v-for="(file, fileIndex) in filePreviews" :key="fileIndex" class="preview-item">
                    <p>
                      {{ file.originalName || file.name }} ({{ formatFileSize(file.size) }}) 
                      <button type="button" v-if="file.url" @click="downloadFile(fileIndex)">{{ $t('download') }}</button>
                      <button type="button" @click="openShareModal(fileIndex)">{{ $t('share') }}</button>
                      <button type="button" @click="removeFile(fileIndex)">{{ $t('delete') }}</button>
                    </p>
                    <!-- 分享设置 -->
                    <div v-if="showShareModal" class="share-modal">
                      <div class="modal-content">
                        <h3>{{ $t('ShareSettings') }}</h3>
                        <label>
                          {{ $t('Setpassword') }}
                          <input v-model="shareForm.password" type="password" :placeholder="$t('Enterthesharingpassword')" />
                        </label>
                        <label>
                          {{ $t('Validityperiod') }}
                          <select v-model="shareForm.expiresIn">
                            <option value="1">{{ $t('oneDay') }}</option>
                            <option value="7">{{ $t('sevenDays') }}</option>
                            <option value="30">{{ $t('thirtyDays') }}</option>
                            <option value="0">{{ $t('Unlimited') }}</option>
                          </select>
                        </label>
                        <button @click="createShareLink">{{ $t('GenerateShareLink') }}</button>
                        <button @click="showShareModal = false">{{ $t('Cancel') }}</button>
                        <div v-if="shareLink" class="share-link">
                          <label>{{ $t('ShareLink') }}</label>
                          <input :value="shareLink" readonly />
                          <button @click="copyShareLink">{{ $t('CopyLink') }}</button>
                        </div>
                      </div>
                    </div>
                    <div v-if="uploadProgress.files[fileIndex]" class="progress-bar">
                      <progress :value="uploadProgress.files[fileIndex]" max="100"></progress>
                      <span>{{ uploadProgress.files[fileIndex] }}% ({{ formatFileSize(file.size) }})</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="formData.file_urls.length" class="file-error">
                  <p v-for="(url, urlIndex) in formData.file_urls" :key="urlIndex">
                    {{ $t('Thefilecannotbeloaded') }} {{ url }}
                  </p>
                </div>
              </div>
              <div class="options" v-if="isLoggedIn">
                <label>
                  {{ $t ('Checktheencryptednote') }}
                  <input type="checkbox" v-model="formData.is_encrypted">
                </label>
              </div>
            </div>
            
            <div class="footer">
              <button type="submit" :disabled="isSaving">
                {{ isSaving ? $t('Saving...') : $t('save') }}
              </button>
            </div>

            <iframe src="https://afdian.com/leaflet?slug=xkkie" width="640" scrolling="no" height="200" frameborder="0"></iframe>
          </form>
        </div>
      </div>
    </div>
    <div v-if="showModal" class="image-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <img :src="enlargedImage" alt="放大图片" class="enlarged-img" />
        <button class="close-button" @click="closeModal">{{ $t('close') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Quill from 'quill';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
const { t, locale } = useI18n();

const showShareModal = ref(false);
const shareForm = ref({
  fileIndex: null,
  password: '',
  expiresIn: '7', // 默认7天
});

const shareLink = ref(''); // 新增状态存储分享链接
const isLinkGenerated = ref(false); // 新增：链接生成状态，用于控制按钮禁用等

const encryptionKey = ref(''); // 动态密钥
const token = localStorage.getItem('token');
if (token) {
  try {
    const decoded = JSON.parse(atob(token.split('.')[1])); // 解码 JWT payload
    encryptionKey.value = decoded.encryption_key || '92fa6b717f5767c7'; // 从 JWT 获取
  } catch (e) {
    console.error('解析 JWT 失败:', e);
    encryptionKey.value = '92fa6b717f5767c7'; // 回退
  }
} else {
  encryptionKey.value = '92fa6b717f5767c7'; // 未登录使用默认密钥
}

// 创建 axios 实例
const axiosInstance = axios.create({
  baseURL: 'https://a.410121.xyz/api',
  timeout: 10000,
});

// Router and route
const route = useRoute();
const router = useRouter();

// Reactive state
const formData = reactive({
  title: '',
  content: '',
  is_encrypted: false,
  version: 1,
  images: [],
  image_urls: [],
  files: [],
  file_urls: [],
  original_file_names: [],
});

const isEditMode = ref(false);
const isSaving = ref(false);
const imagePreviews = ref([]);
const filePreviews = ref([]);
const isLoggedIn = ref(!!localStorage.getItem('isLoggedIn'));
const uploadProgress = reactive({
  images: {},
  files: {},
});
const showModal = ref(false);
const enlargedImage = ref('');
let quill = null;

// Message notification
const $message = {
  error: (msg) => alert(`Error: ${msg}`),
  success: (msg) => alert(`Success: ${msg}`),
};

// 监听 locale 变化，更新 Quill placeholder（现在有效）
watch(locale, () => {
  if (quill) {
    quill.root.dataset.placeholder = t('Pleaseentercontent')
  }
})

// 初始化 Quill 编辑器
onMounted(() => {
  quill = new Quill('#editor', {
    theme: 'snow',
    modules: {
      toolbar: [
        ['bold', 'underline'], // 支持加粗和下划线
      ],
    },
    placeholder: t('Pleaseentercontent'),
  });

  // 同步 Quill 内容到 formData.content
  quill.on('text-change', () => {
    formData.content = quill.root.innerHTML;
  });

  // 加载笔记
  const noteId = route.params.id;
  if (noteId) {
    isEditMode.value = true;
    loadNotepad(noteId);
  }
});

// 解密数据
const decryptData = (encryptedData) => {
  if (!encryptedData || typeof encryptedData !== 'string') return '';
  if (!encryptionKey.value) return encryptedData;

  // 兼容后端 hex 格式 (iv:encrypted)
  if (encryptedData.includes(':')) {
    try {
      const [ivHex, encryptedHex] = encryptedData.split(':');
      const iv = CryptoJS.enc.Hex.parse(ivHex);
      const encrypted = CryptoJS.enc.Hex.parse(encryptedHex);
      const key = CryptoJS.enc.Hex.parse(encryptionKey.value.padEnd(32, encryptionKey.value)); // 确保 32 字节
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: encrypted },
        key,
        { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
      );
      return decrypted.toString(CryptoJS.enc.Utf8) || encryptedData;
    } catch (error) {
      console.error('Hex 格式解密失败:', error);
      return encryptedData;
    }
  }
  return encryptedData;
};

// 获取远程文件大小
const getFileSize = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    if (response.ok) {
      const contentLength = response.headers.get('Content-Length');
      return contentLength ? parseInt(contentLength, 10) : 0;
    }
    return 0;
  } catch (error) {
    console.error('获取文件大小失败:', url, error);
    return 0;
  }
};

// 加载笔记
const loadNotepad = async (id) => {
  if (!isLoggedIn.value) {
    // 未登录：从本地加载（保持不变）
    const localData = JSON.parse(localStorage.getItem('localNotepads') || '[]');
    const note = localData.find(n => n.localId === id);
    if (note) {
      Object.assign(formData, {
        title: note.title,
        content: note.content,
        is_encrypted: false,
        version: 1,
        image_urls: note.image_urls || [],
        file_urls: note.file_urls || [],
        original_file_names: [], // 添加默认值
      });
      quill.root.innerHTML = note.content;
      imagePreviews.value = note.image_urls.map(url => url.startsWith('data:') ? url : `data:image/png;base64,${url}`);
      filePreviews.value = note.file_urls.map(url => ({ name: '本地文件', size: 0, url }));
    } else {
      $message.error('本地笔记未找到');
    }
    return;
  }
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('用户未登录，请先登录');
    const response = await axiosInstance.get(`/notepads/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.data.code === 200 && response.data.data) {
      const note = response.data.data;
      formData.title = decryptData(note.title);  // 应用解密
      quill.root.innerHTML = decryptData(note.content);  // 应用解密
      formData.is_encrypted = note.is_encrypted;
      formData.version = note.version;
      formData.image_urls = note.image_url || [];
      formData.file_urls = note.file_url || [];
      
      let imageUrls = note.image_url
        ? Array.isArray(note.image_url)
          ? note.image_url
          : JSON.parse(note.image_url)
        : [];
      let fileUrls = note.file_url
        ? Array.isArray(note.file_url)
          ? note.file_url
          : JSON.parse(note.file_url)
        : [];
      let originalFileNames = note.original_file_names // 直接使用后端返回的字段
        ? Array.isArray(note.original_file_names)
          ? note.original_file_names
          : JSON.parse(note.original_file_names)
        : [];

      // 直接使用后端返回的解密数据和 original_file_names
      Object.assign(formData, {
        title: note.title || '', // 解密已在前面处理
        content: note.content || '', // 解密已在前面处理
        is_encrypted: !!note.is_encrypted,
        version: note.version || 1,
        images: [],
        image_urls: imageUrls,
        files: [],
        file_urls: fileUrls,
        original_file_names: originalFileNames, // 使用定义的 originalFileNames
      });

      quill.root.innerHTML = note.content; // 解密已在前面处理
      imagePreviews.value = [];
      const loadImages = async () => {
      const token = localStorage.getItem('token'); // 获取 token 用于 fetch
        for (const url of imageUrls) {
          const fullUrl = `https://a.410121.xyz${url.startsWith('/') ? '' : '/'}${url}?t=${Date.now()}`;
          try {
            // 用 fetch 带 token 请求图片 Blob 数据
            const response = await fetch(fullUrl, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`, // 携带认证头
                'Content-Type': 'image/*', // 提示图片类型
              },
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob(); // 获取 Blob
            const blobUrl = URL.createObjectURL(blob); // 生成临时 blob URL

            // 验证图片加载（用 Image 对象测试）
            await new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous'; // 保持原设置（blob URL 无跨域问题）
              img.onload = () => {
                // 加载成功，添加到预览
                imagePreviews.value.push(blobUrl);
                resolve();
              };
              img.onerror = () => {
                URL.revokeObjectURL(blobUrl); // 失败时释放
                reject(new Error('图片格式无效'));
              };
              img.src = blobUrl;
            });
          } catch (error) {
            console.error(`加载图片失败: ${url}`, error);
            $message.error(`无法加载图片: ${url}`);
            // 从 formData 中移除无效 URL
            const invalidIndex = formData.image_urls.findIndex(u => u === url);
            if (invalidIndex !== -1) {
              formData.image_urls.splice(invalidIndex, 1);
            }
          }
        }
      };
      await loadImages();

      filePreviews.value = await Promise.all(fileUrls.map(async (url, index) => {
        let decodedName;
        try {
          const fileName = url.split('/').pop();
          decodedName = decodeURIComponent(fileName);
        } catch (error) {
          console.error('Failed to decode file name on load:', url, error);
          decodedName = url.split('/').pop();
        }
        const fullUrl = `https://a.410121.xyz${url.startsWith('/') ? '' : '/'}${url}?t=${Date.now()}`;
        const size = await getFileSize(fullUrl);
        return {
          name: originalFileNames[index] || decodedName,
          originalName: originalFileNames[index] || decodedName,
          size,
          url: fullUrl
        };
      }));
    } else {
      throw new Error(response.data.message || '加载失败');
    }
  } catch (error) {
    $message.error(`加载失败: ${error.message || '网络错误'}`);
    if (error.message.includes('未提供认证令牌') || error.message.includes('无效的认证令牌')) {
      localStorage.clear();
      router.push('/login');
    } else {
      router.push({ name: 'Home' });
    }
  }
};

const checkLimits = (type, files) => {
  if (!isLoggedIn.value) {
    const maxCount = 5;
    const maxSize = type === 'image' ? 15 * 1024 * 1024 : 30 * 1024 * 1024;
    if (formData[`${type}s`].length + files.length > maxCount) {
      $message.error(`最多上传${maxCount}个${type === 'image' ? '图片' : '文件'}`);
      return false;
    }
    for (const file of files) {
      if (file.size > maxSize) {
        $message.error(`${type === 'image' ? '图片' : '文件'}大小不能超过 ${maxSize / 1024 / 1024}MB`);
        return false;
      }
    }
  }
  return true;
};

// 添加图片
const addImage = (file) => {
  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    $message.error('仅支持 PNG 或 JPEG 格式');
    return false;
  }
  if (file.size > 20 * 1024 * 1024) {
    $message.error('图片大小不能超过 20MB');
    return false;
  }
  if (formData.images.length >= 10) {
    $message.error('最多上传10张图片');
    return false;
  }
  formData.images.push(file);
  try {
    const previewUrl = URL.createObjectURL(file);
    imagePreviews.value.push(previewUrl);
    return true;
  } catch (error) {
    $message.error('无法生成图片预览');
    formData.images.pop();
    return false;
  }
};

// 处理图片上传
const handleImageUpload = (event) => {
  const files = Array.from(event.target.files);
  if (!checkLimits('image', files)) return;
  if (formData.images.length + files.length > 10) {
    $message.error('最多上传10张图片');
    return;
  }
  files.forEach((file) => {
    addImage(file);
  });
  event.target.value = '';
};

// 处理图片粘贴
const handleImagePaste = (event) => {
  event.preventDefault();
  const items = (event.clipboardData || event.originalEvent.clipboardData).items;
  for (const item of items) {
    if (item.type.indexOf('image') === 0) {
      const file = item.getAsFile();
      if (!file) {
        $message.error('粘贴内容不是有效图片');
        continue;
      }
      addImage(file);
    }
  }
};

// 处理文件上传
const handleFileUpload = (event) => {
  const files = Array.from(event.target.files);
  if (!checkLimits('file', files)) return;
  if (formData.files.length + files.length > 10) {
    $message.error('最多上传10个文件');
    return;
  }
  files.forEach((file) => {
    const allowedExtensions = ['.doc', '.docx', '.exe', '.apk', '.zip', '.rar', '.7z', '.txt', '.msi', '.mp4'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      $message.error('仅支持 Word、EXE、APK、msi、mp4、压缩包或 TXT 格式');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      $message.error('文件大小不能超过 50MB');
      return;
    }
    // 直接使用原始文件名
    formData.files.push(file); // 移除 encodeURIComponent 和 decodeURIComponent
    filePreviews.value.push({
      name: file.name, // 使用原始文件名
      originalName: file.name,
      size: file.size,
      url: null,
    });
    console.log('Added file to previews:', file.name);
  });
  event.target.value = '';
};

// 下载方式
const downloadFile = async (fileIndex) => {
  try {
    const file = filePreviews.value[fileIndex];
    if (!file.url) throw new Error('文件 URL 未找到');
    const token = localStorage.getItem('token');
    const response = await axiosInstance.get(file.url, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = file.originalName;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(link.href);
    $message.success('下载成功');
  } catch (error) {
    $message.error(`下载失败: ${error.message}`);
    console.error('下载错误:', error);
  }
};

const openShareModal = (fileIndex) => {
  shareForm.value.fileIndex = fileIndex;
  shareForm.value.password = '';
  shareForm.value.expiresIn = '7';
  shareLink.value = ''; // 重置分享链接
  isLinkGenerated.value = false; // 重置生成状态
  showShareModal.value = true;
};

// 生成分享链接
const createShareLink = async () => {
  try {
    const fileIndex = shareForm.value.fileIndex;
    const file = filePreviews.value[fileIndex];
    if (!file) throw new Error('文件未找到');

    let fileUrl = file.url;
    let originalName = file.originalName || file.name;

    // 如果文件未上传，执行上传
    if (!fileUrl && formData.files[fileIndex]) {
      const fileToUpload = formData.files[fileIndex];
      const formDataUpload = new FormData();
      formDataUpload.append('files', fileToUpload);
      formDataUpload.append('is_encrypted', formData.is_encrypted ? 'true' : 'false');
      const token = localStorage.getItem('token');
      const response = await axiosInstance.post('/api/upload/files', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 1;
          const loaded = progressEvent.loaded;
          const percent = Math.round((loaded / total) * 100);
          uploadProgress.files[fileIndex] = percent;
        },
      });
      if (response.data.code !== 200 || !response.data.data[0]) {
        throw new Error(response.data.message || '上传文件失败');
      }
      fileUrl = response.data.data[0].path; // 使用返回的 path
      originalName = response.data.data[0].originalName;
      filePreviews.value[fileIndex].url = `https://a.410121.xyz${fileUrl}`;
      filePreviews.value[fileIndex].originalName = originalName;
      filePreviews.value[fileIndex].size = response.data.data[0].size;
    }

    if (!fileUrl) throw new Error('文件 URL 未找到');

    // 提取相对路径
    fileUrl = fileUrl.replace('https://a.410121.xyz', '');
    if (fileUrl.startsWith('/Uploads/')) {
      fileUrl = fileUrl.replace('/Uploads/', '');
    } else if (fileUrl.startsWith('/')) {
      fileUrl = fileUrl.slice(1);
    }

    console.log('生成分享链接，文件:', { fileUrl, originalName });

    const token = localStorage.getItem('token');
    const shareResponse = await axiosInstance.post(
      '/share/file',
      {
        filename: fileUrl,
        originalName,
        password: shareForm.value.password || null,
        expiresIn: shareForm.value.expiresIn,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (shareResponse.data.code !== 200) {
      throw new Error(shareResponse.data.message || '生成分享链接失败');
    }

    const shareToken = shareResponse.data.data.token;
    shareLink.value = `https://410121.xyz/share/${shareToken}`;
    // 保持模态框打开，显示链接，不关闭页面或模态框
    showShareModal.value = true;
    // 修改：设置生成状态，无任何 $message.success 提示，直接显示链接
    isLinkGenerated.value = true;
    console.log('分享链接生成成功:', shareLink.value); // 只在控制台日志，无 UI 干扰
  } catch (error) {
    console.error('生成分享链接失败:', error);
    $message.error(`生成分享链接失败: ${error.response?.data?.message || error.message}`);
    // 修改：错误时也不关闭模态，保持用户可重试
  }
};

// 复制分享链接
const copyShareLink = async () => {
  if (!shareLink.value) {
    $message.error('请先生成分享链接');
    return;
  }
  try {
    await navigator.clipboard.writeText(shareLink.value);
    // 修改：移除成功提示，静默复制
    console.log('分享链接已复制到剪贴板');
  } catch (err) {
    const textArea = document.createElement('textarea');
    textArea.value = shareLink.value;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      // 修改：移除成功提示
      console.log('分享链接已复制到剪贴板（使用备用方法）');
    } catch (copyErr) {
      console.error('复制失败:', copyErr);
      $message.error(`无法自动复制，请手动复制链接: ${shareLink.value}`);
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

// 阻止非粘贴输入
const preventInput = (event) => {
  if (event.key !== 'v' && !event.ctrlKey) {
    event.preventDefault();
  }
};

// 删除图片
const removeImage = (index) => {
  if (index < 0 || index >= imagePreviews.value.length) return; // 安全检查

  const previewUrl = imagePreviews.value[index];
  URL.revokeObjectURL(previewUrl); // 总是撤销blob URL

  if (index < formData.image_urls.length) {
    // 已上传图片：从 image_urls 中删除
    formData.image_urls.splice(index, 1);
  } else {
    // 新上传图片：从 images 中删除（调整索引）
    const newIndex = index - formData.image_urls.length;
    if (newIndex < formData.images.length) {
      formData.images.splice(newIndex, 1);
    }
  }

  imagePreviews.value.splice(index, 1); // 最后删除预览
};

// 删除文件
const removeFile = async (index) => {
    const filePreview = filePreviews.value[index];
    if (filePreview.url) {
        const urlIndex = formData.file_urls.findIndex((url) =>
            filePreview.url.includes(url)
        );
        if (urlIndex !== -1) {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const noteId = route.params.id; // 使用当前笔记 ID
                    const fileUrl = formData.file_urls[urlIndex];
                    const response = await axiosInstance.delete(`/notepads/${noteId}/delete-file?file_url=${encodeURIComponent(fileUrl)}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.data.code === 200) {
                        formData.file_urls.splice(urlIndex, 1);
                        // 更新 original_file_names（如果后端返回）
                        if (response.data.data && response.data.data.original_file_names) {
                            formData.original_file_names = response.data.data.original_file_names; // 假设前端也有这个字段
                        }
                    }
                }
            } catch (error) {
                $message.error(`删除文件失败: ${error.message}`);
                console.error('删除文件错误:', error.response ? error.response.data : error);
                return;
            }
        }
    } else {
        formData.files.splice(index, 1);
    }
    filePreviews.value.splice(index, 1);
};

// 保存笔记
const saveNotepad = async () => {
  // 修改：如果分享模态打开，静默阻止保存
  if (showShareModal.value) {
    return;
  }

  isSaving.value = true;
  if (!isLoggedIn.value) {
    $message.error('请先登录');
    isSaving.value = false;
    return;
  }
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('用户未登录，请先登录');
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title.trim());
    formDataToSend.append('content', formData.content);
    formDataToSend.append('is_encrypted', formData.is_encrypted ? 'true' : 'false');
    formDataToSend.append('version', formData.version.toString());
    formDataToSend.append('image_urls', JSON.stringify(formData.image_urls));
    formDataToSend.append('file_urls', JSON.stringify(formData.file_urls));
    formDataToSend.append('is_pending_sync', formData.is_pending_sync ? 'true' : 'false');

    formData.images.forEach((image, index) => {
      formDataToSend.append('images', image);
      uploadProgress.images[index] = 0;
    });

    formData.files.forEach((file, index) => {
      formDataToSend.append('files', file);
      uploadProgress.files[index] = 0;
    });

    const config = {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress: (progressEvent) => {
        const total = progressEvent.total || 1;
        const loaded = progressEvent.loaded;
        const percent = Math.round((loaded / total) * 100);
        formData.images.forEach((_, index) => {
          uploadProgress.images[index] = percent;
        });
        formData.files.forEach((_, index) => {
          uploadProgress.files[index] = percent;
        });
      },
    };
    let response;
    if (isEditMode.value) {
      response = await axiosInstance.put(`/notepads/${route.params.id}`, formDataToSend, config);
    } else {
      response = await axiosInstance.post('/notepads', formDataToSend, config);
    }

    if (response.data.code === 200 || response.data.code === 201) {
      const returnedData = response.data.data || {}; // 防止 data 为 undefined
      // 修改：保存成功后不清除内容，不跳转，保持编辑状态
      uploadProgress.images = {};
      uploadProgress.files = {};
      $message.success(isEditMode.value ? '更新成功' : '保存成功');
      // 移除：navigateBack(); // 不跳转
      // 移除：Object.assign(formData, { ... }); // 不清空
      // 移除：quill.root.innerHTML = ''; // 不清空编辑器
      // 移除：imagePreviews.value = []; 等
    } else {
      throw new Error(response.data.message || '保存失败');
    }
  } catch (error) {
    $message.error(`保存失败: ${error.message || '网络错误'}`);
    console.error('保存错误:', error.response ? error.response.data : error);
  } finally {
    isSaving.value = false;
    uploadProgress.images = {};
    uploadProgress.files = {};
  }
};

const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// 清空输入
const clearInputs = () => {
  if (isEditMode.value) {
    if (!confirm('清空内容将删除所有未保存的更改（包括图片和文件），是否继续？')) {
      return;
    }
  } else if (!confirm('确定清空所有内容？此操作会清除图片和文件！')) {
    return;
  }
  Object.assign(formData, {
    title: '',
    content: '',
    is_encrypted: false,
    version: 1,
    images: [],
    image_urls: [],
    files: [],
    file_urls: [],
  });
  quill.root.innerHTML = '';
  imagePreviews.value.forEach((preview) => {
    if (preview.startsWith('blob:')) URL.revokeObjectURL(preview);
  });
  imagePreviews.value = [];
  filePreviews.value = [];
  uploadProgress.images = {};
  uploadProgress.files = {};
  showModal.value = false;
};

// 返回导航
const navigateBack = () => {
  if (showShareModal.value) {
    // 修改：关闭模态时，重置分享状态，但不触发表单清空/跳转
    showShareModal.value = false;
    shareLink.value = '';
    isLinkGenerated.value = false; // 修改：重置生成状态
    return; // 明确返回，不执行下方逻辑
  }
  if (
    formData.title.trim() ||
    formData.content.trim() ||
    formData.images.length ||
    formData.files.length ||
    formData.image_urls.length ||
    formData.file_urls.length
  ) {
    if (confirm('当前内容未保存，确定返回吗？')) {
      imagePreviews.value.forEach((preview) => {
        if (preview.startsWith('blob:')) URL.revokeObjectURL(preview);
      });
      router.push({ name: 'Home' });
    }
  } else {
    router.push({ name: 'Home' });
  }
};

// 显示放大图片
const showEnlargedImage = (imageUrl) => {
  enlargedImage.value = imageUrl;
  showModal.value = true;
};

// 关闭模态框
const closeModal = () => {
  showModal.value = false;
  enlargedImage.value = '';
};
</script>

<style scoped>
.w {
  width: 1700px;
  margin: 0 auto;
}
img {
  width: 100%;
  height: 100%;
  display: block;
}
#home {
  width: 100%;
}
#contentBox {
  width: 100%;
  min-height: 100vh;
  background-repeat: no-repeat;
  margin-left: 200px;
}
.registervideoContainer {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  margin-left: 0;
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: -1;
  background-image: url(../assets/2.jpg);
  filter: blur(6px);
}
.encrypt-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #333;
}
.encrypt-label input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}
.edit-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
}
input,
textarea {
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  width: 100%;
}
textarea {
  resize: vertical;
  min-height: 200px;
  max-height: 600px;
  height: auto;
  flex: none;
}
.footer {
  display: flex;
  justify-content: flex-end;
}
button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}
button:hover {
  background-color: #0056b3;
}
button:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}
.upload-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}
.file-input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
}
.paste-area {
  border: 1px dashed #ccc;
  padding: 10px;
  min-height: 50px;
  border-radius: 4px;
  background-color: #f9f9f9;
  cursor: text;
}
.paste-area:focus {
  outline: none;
  border-color: #007bff;
}
.image-preview {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: 10px;
  padding: 10px 0;
  scrollbar-width: thin;
}
.image-preview::-webkit-scrollbar {
  height: 8px;
}
.image-preview::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}
.preview-item {
  flex: 0 0 auto;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.preview-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}
.preview-item button {
  padding: 5px 10px;
  font-size: 12px;
  background-color: #dc3545;
}
.preview-item button:hover {
  background-color: #c82333;
}
.image-error {
  color: #dc3545;
  margin-top: 10px;
}
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.image-preview, .file-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 10px;
}
.preview-item {
  width: 200px;
  border: 1px solid #eee;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
}
.modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.enlarged-img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}
.close-button {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.close-button:hover {
  background-color: #c82333;
}
/* 分享 */
.share-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.share-modal .modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.share-modal label {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.share-modal input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.share-modal button {
  padding: 8px 16px;
  border-radius: 4px;
}

.share-modal button:first-of-type {
  background-color: #007bff;
}

.share-modal button:last-of-type {
  background-color: #dc3545;
}
@media screen and (max-width: 1600px) {
  .w {
    max-width: 1400px;
  }
}
@media screen and (max-width: 1400px) {
  .w {
    max-width: 1200px;
  }
  .registervideoContainer {
    margin-left: -2%;
  }
}
@media screen and (max-width: 1200px) {
  .w {
    max-width: 1000px;
  }
}
@media screen and (max-width: 1000px) {
  .w {
    max-width: 100%;
  }
  .registervideoContainer {
    margin-left: 0;
  }
  #contentBox {
    margin-left: 0;
  }
}
@media screen and (max-width: 550px) {
  .w {
    max-width: 100%;
  }
  .registervideoContainer {
    margin-left: 0;
  }
  .edit-container {
    padding: 10px;
  }
  input,
  textarea {
    padding: 8px;
  }
  button {
    padding: 8px 16px;
  }
  .preview-item {
    width: 150px;
  }
  .preview-img {
    height: 100px;
  }
}
</style>